import * as Sentry from "@sentry/nextjs";
import type { Tuple } from "@litres-libs/types";
import first from "lodash/first";
import isArray from "lodash/isArray";
import isObject from "lodash/isObject";
import last from "lodash/last";

/**
 * @typedef {Object} SentryLoggerOptions
 * @property {Error} errorClass - кастомный класс ошибки
 * @property {string} logLevel - уровень логирования (Sentry.SeverityLevel)
 */
interface SentryLoggerOptions {
  errorClass?: new (message: string) => Error;
  logLevel?: Sentry.SeverityLevel;
}

/**
 * Логгер в sentry
 *
 * @param {String} message - текст ошибки
 * @param {Tuple} contextProperties - кортеж [имя контекста, properties]
 * @param {Object} tags - теги
 * @param {SentryLoggerOptions} options
 *
 * @example
 *
 * SentryLogger.log(
 *   'Foundation', // context name
 *   'My error message', // error text
 *   ['params', { user_type: 'abc', country: 'uk' }], // context props
 *   {
 *     tag1: 'tag1_value',
 *   },
 *   {
 *    logLevel: logLevel.Error, // optionsl
 *    errorClass: MyCustomErrorClass, // optional
 *   }
 *);
 */
export function sentryLogger(
  message: string,
  contextProperties?: Tuple<string, Record<string, unknown>>,
  tags?: Record<string, string>,
  options?: SentryLoggerOptions
) {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const ErrorClass = options?.errorClass || Error;
  const sentryErrorLevel = options?.logLevel || "log";

  if (contextProperties && isArray(contextProperties)) {
    Sentry.setContext(first(contextProperties), last(contextProperties));
  }

  Sentry.withScope(scope => {
    if (isObject(tags)) {
      Object.entries(tags).forEach(([name, value]) => {
        scope.setTag(name, value);
      });
    }
    scope.setLevel(sentryErrorLevel);
    Sentry.captureException(new ErrorClass(message));
  });
}

export const sentryBreadcrumb = Sentry.addBreadcrumb;
